import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { FormulaCatalogoResponse } from '../../responses/formula/formula-catalogo.response';
import { FormulaCatalogoValidacaoResponse } from '../../responses/formula/formula-catalogo-validacao.response';
import { FormulaCatalogoValidarPersonalizacoesRequest } from '../../requests/formula/formula-catalogo-validar-personalizacoes.request';
import { FormulaCatalogoGrupoStruct } from '../../structs/formula/formula-catalogo-grupo.struct';
import { FormulaCatalogoItemStruct } from '../../structs/formula/formula-catalogo-item.struct';
import { VariavelStruct } from '../../structs/formula/variavel.struct';
import { TipoOrigemVariavelEnum } from '@shared/enums/formula/tipo-origem-variavel.enum';
import { FormulaCatalogoContextoStruct } from '../../structs/formula/formula-catalogo-contexto.struct';
import { FormulaCatalogoFormulaStruct } from '../../structs/formula/formula-catalogo-formula.struct';
import { FormulaCatalogoVariavelStruct } from '../../structs/formula/formula-catalogo-variavel.struct';

/** Acesso ao catálogo genérico de fórmulas do Motor Fiscal. */
@Injectable({ providedIn: 'root' })
export class FormulaCatalogoService extends CommonService {
    private readonly apiUrl = environment.urlApiMotorFiscal + 'FormulaCatalogo';

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getContexto(chaveRaiz = 'natureza-operacao'): Observable<FormulaCatalogoResponse> {
        return this.httpClient.get<FormulaCatalogoResponse>(`${this.apiUrl}/contextos/${encodeURIComponent(chaveRaiz)}`, {
            ...this.addHeaderToken(),
        }).pipe(
            map(response => ({ ...response, grupos: response.contexto ? this.converterContextosEmGrupos(response.contexto) : [] })),
            catchError(this.handleError),
        );
    }

    public validarPersonalizacoes(request: FormulaCatalogoValidarPersonalizacoesRequest): Observable<FormulaCatalogoValidacaoResponse> {
        return this.httpClient.post<FormulaCatalogoValidacaoResponse>(
            `${this.apiUrl}/validar-personalizacoes`,
            request,
            this.addHeaderToken(),
        ).pipe(catchError(this.handleError));
    }

    private converterContextosEmGrupos(raiz: FormulaCatalogoContextoStruct): FormulaCatalogoGrupoStruct[] {
        const contextosVisiveis = raiz.filhos.length > 0 ? raiz.filhos : [raiz];
        return contextosVisiveis.map(contexto => this.mapearContexto(contexto));
    }

    private mapearContexto(contexto: FormulaCatalogoContextoStruct): FormulaCatalogoGrupoStruct {
        return {
            chave: contexto.chave,
            grupo: contexto.nome,
            googleIconClass: contexto.googleIconClass,
            formulas: contexto.formulas.map(formula => ({
                ...this.mapearFormula(formula),
                contextoChave: contexto.chave,
                criteriosContexto: contexto.criterios,
            })),
            filhos: contexto.filhos.map(filho => this.mapearContexto(filho)),
        };
    }

    private mapearFormula(formula: FormulaCatalogoFormulaStruct): FormulaCatalogoItemStruct {
        return {
            chaveFormula: formula.chaveFormula,
            descricao: formula.descricao,
            isPersonalizavel: formula.isPersonalizavel,
            variavelPrincipal: this.mapearVariavel(formula.variavelSaida),
            variaveis: formula.variaveisPermitidas.map(variavel => this.mapearVariavel(variavel)),
            variaveisPermitidas: formula.variaveisPermitidas.map(variavel => this.mapearVariavel(variavel)),
            modelos: formula.modelos.map(modelo => ({ ...modelo })),
            chaveFormulaModeloEmpresa: formula.configuracaoEfetiva.chaveFormulaModelo,
        };
    }

    private mapearVariavel(variavel: FormulaCatalogoVariavelStruct): VariavelStruct {
        return {
            nome: variavel.tipoOrigemVariavel === TipoOrigemVariavelEnum.Sistema || variavel.tipoOrigemVariavel === TipoOrigemVariavelEnum.Parametro
                ? variavel.nome.toUpperCase()
                : variavel.nome,
            chave: variavel.chave,
            pathTagXml: variavel.pathTagXml,
            descricao: variavel.descricao,
            observacao: variavel.observacao,
            tipoDadoVariavel: variavel.tipoDadoVariavel,
            tipoEscopoVariavel: variavel.tipoEscopoVariavel,
            tipoOrigemVariavel: variavel.tipoOrigemVariavel,
            isVariavelGrupo: variavel.isVariavelGrupo,
            possuiFormulaAplicavel: variavel.possuiFormulaAplicavel,
            chavesGruposEquivalencia: variavel.chavesGruposEquivalencia,
        };
    }
}
